import * as React from 'react';
import { useState } from 'react';
import { ArrowForwardIos as RightIcon } from '@mui/icons-material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import RadarChart from './Ui/RadarChart/RadarChart';
import MiniCalendar, {
  isCalendarEventOnDay,
} from './Ui/DashBoardCalendar';
import dayjs from 'dayjs';
import Icon from '../Assets/Icon/CustomIcons';
import Global from '../Services/Common/Global';
import ScheduleDetailDialog from './Ui/Dialog/ScheduleDetailDialog';
import ChecklistDialog, {
  resolveAlarmTitleToChecklistTabIndex,
} from './Ui/Dialog/CheckListDialog';
import TaskFormDialog from './Ui/Dialog/TaskFormDialog';
import CompleteWorkListDialog from './Ui/Dialog/CompleteWorkListDialog';

const PRIORITY_DISPLAY = {
  1: {
    label: '긴급',
    badgeClass: 'bg-red-50 text-red-600',
    detailKey: 'urgent',
  },
  2: {
    label: '보통',
    badgeClass: 'bg-blue-50 text-blue-600',
    detailKey: 'normal',
  },
  3: {
    label: '낮음',
    badgeClass: 'bg-gray-50 text-gray-500',
    detailKey: 'low',
  },
};

const STATUS_DISPLAY = {
  10: {
    label: '대기',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    detailKey: 'pending',
  },
  20: {
    label: '진행중',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    detailKey: 'in_progress',
  },
  90: {
    label: '완료',
    color: 'bg-green-100 text-green-700 border-green-200',
    detailKey: 'completed',
  },
};

function SectionHeader({ title, isOnMore = true, onMore, moreLabel = 'more' }) {
  return (
    <div className="px-6 py-4 border-b border-[#e1e8ed] flex justify-between items-center bg-gray-50/50">
      <h2 className="font-bold text-[#2c3e50] text-sm flex items-center gap-2">
        {title}
      </h2>
      {isOnMore && (
        <button
          onClick={onMore}
          className="text-[11px] font-bold text-[#77b054] hover:underline flex items-center gap-0.5"
        >
          {moreLabel}
          <RightIcon className="text-[10px]! ml-0.5" />
        </button>
      )}
    </div>
  );
}

export default function DashBoard({
  noticeData = [],
  newsData,
  seriousData,
  seriousSummary,
  calendarEvents = [],
  myCalendars = [],
  onDateChange,
  alarmData,
  workListData = [],
  search2,
  alarmDetailData,
  completeWorkListData,
  SaveLogic4,
  handleSaveLogic4,
  handleReadAll,
  handleSaveWork = () => {},
  handleDeleteWork = () => {},
  handleStatusChange = () => {},
  handlePriorityChange = () => {},
  handlePinWork = () => {},
  search6,
  onMonthChange,
}) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [completeDialog, setCompleteDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState({
    open: false,
    data: {},
  });
  const [checklistDialog, setChecklistDialog] = useState(false);
  const [checklistInitialTabIndex, setChecklistInitialTabIndex] = useState(0);
  const [workFormDialog, setWorkFormDialog] = useState({
    open: false,
    data: null,
  });
  const [openStatusMenu, setOpenStatusMenu] = useState(null);
  const [menuDirection, setMenuDirection] = useState({});
  const [openPriorityMenu, setOpenPriorityMenu] = useState(null);
  const [priorityMenuDirection, setPriorityMenuDirection] = useState({});
  const formatDateLabel = (date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    return `${month}월 ${day}일 ${weekdays[date.getDay()]}요일`;
  };
  const filteredEvents = calendarEvents.filter((item) =>
    isCalendarEventOnDay(item, selectedDate)
  );

  const getDday = (startDt) => {
    if (!startDt) return '';
    const today = dayjs().startOf('day');
    const targetDate = dayjs(startDt).startOf('day');
    const diffDays = targetDate.diff(today, 'day');
    if (diffDays === 0) return 'D-day';
    return diffDays > 0 ? `D-${diffDays}` : `D+${Math.abs(diffDays)}`;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    onDateChange?.(date);
  };

  const handleScheduleClick = (ev) => {
    setScheduleDialog({ open: true, data: ev });
  };

  const handleTaskClick = (task) => {
    const toYmd = (v) => {
      if (v == null || v === '') return '';
      if (typeof v === 'string') return v.substring(0, 10);
      const d = dayjs(v);
      return d.isValid() ? d.format('YYYY-MM-DD') : '';
    };
    setWorkFormDialog({
      open: true,
      data: {
        workId: task.workId,
        title: task.title || '',
        contents: task.contents || '',
        priorityBit: task.priorityBit ?? 2,
        statusCd: String(task.statusCd ?? '10'),
        startDt: toYmd(task.startDt),
        dueDt: toYmd(task.dueDt),
        complDt: task.complDt || '',
        name: task.name,
        userName: task.userName,
        photo: task.photo,
      },
    });
  };

  const handleAddNewTask = () => {
    setWorkFormDialog({ open: true, data: null });
  };

  return (
    <div className="flex flex-col gap-6 w-full min-h-full p-6 pt-2 rounded-3xl">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* ── 좌측 컬럼 (8/12) ── */}
        <div className="lg:col-span-8 flex flex-col gap-6 min-h-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* 1. 안전보건 일정 관리 */}
            <section className="lg:col-span-7 bg-white border border-[#e1e8ed] rounded-[20px] shadow-sm overflow-hidden text-black flex flex-col">
              <div className="px-6 py-4 border-b border-[#e1e8ed] flex justify-between items-center bg-gray-50/50">
                <p className="font-bold text-[#77b054] text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#77b054] rounded-full" />
                  안전보건 일정 관리
                </p>
                <button
                  onClick={() => navigate('/Schedule')}
                  className="text-[11px] font-bold text-[#7f8c8d] hover:text-[#77b054] transition-colors flex items-center gap-1"
                >
                  스케줄관리 이동 <RightIcon className="text-[11px]!" />
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                  <MiniCalendar
                    calendarEvents={calendarEvents}
                    onDateSelect={handleDateSelect}
                    onMonthChange={onMonthChange}
                  />
                  <div className="xl:col-span-7 flex flex-col h-58.75">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-bold text-[#2c3e50]">
                        {formatDateLabel(selectedDate)}
                      </span>
                      {filteredEvents.length > 0 && (
                        <span className="text-[11px] font-bold text-[#77b054] bg-[#77b054]/10 px-2 py-0.5 rounded-md border border-[#77b054]/20">
                          {filteredEvents.length}개의 일정
                        </span>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
                      {filteredEvents.length > 0 ? (
                        filteredEvents.map((ev, i) => (
                          <div
                            key={ev.id || i}
                            onClick={() => handleScheduleClick(ev)}
                            className="border rounded-[15px] px-4 py-3 transition-all group bg-white border-[#e1e8ed] cursor-pointer hover:border-[#77b054] hover:shadow-sm"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="text-sm font-bold text-[#2c3e50] line-clamp-1">
                                {ev.title || ev.description || '제목 없음'}
                              </h3>
                              <span className="text-[10px] font-bold text-[#77b054] bg-[#77b054]/10 px-2 py-0.5 rounded-full shrink-0">
                                {getDday(ev.startDt)}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-[11px] font-medium text-[#7f8c8d]">
                              <div className="flex items-center gap-1.5">
                                <Icon.Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="leading-none">
                                  {ev.allDay === 'Y'
                                    ? '종일'
                                    : `${dayjs(ev.startDt).format('HH:mm')} ~ ${dayjs(ev.endDt).format('HH:mm')}`}
                                </span>
                              </div>
                              {ev.location && (
                                <div className="flex items-center gap-1.5">
                                  <Icon.Location className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                  <span className="leading-none truncate max-w-37.5">
                                    {ev.location}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2 h-full">
                          <span className="text-xs font-medium">
                            등록된 일정이 없습니다
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 2. 확인사항 */}
            <section className="lg:col-span-5 bg-white border border-[#e1e8ed] rounded-[20px] shadow-sm overflow-hidden flex flex-col">
              <SectionHeader
                title="확인사항"
                moreLabel="전체보기"
                onMore={() => {
                  search2();
                  setChecklistInitialTabIndex(0);
                  setChecklistDialog(true);
                }}
              />
              <div className="overflow-y-auto custom-scrollbar divide-y divide-[#e1e8ed] h-58.75 relative">
                {alarmData && alarmData.length > 0 ? (
                  alarmData.map((item, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        search2();
                        setChecklistInitialTabIndex(
                          resolveAlarmTitleToChecklistTabIndex(item.title)
                        );
                        setChecklistDialog(true);
                      }}
                      className="px-6 py-3 hover:bg-gray-50 flex justify-between items-center transition-colors cursor-pointer"
                    >
                      <p className="text-sm font-bold text-[#2c3e50]">
                        {item.title}
                      </p>
                      <span className="text-[#77b054] font-black px-3 py-1 rounded-full text-xs">
                        {item.count} 건
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col gap-1 items-center justify-center h-full text-sm text-gray-400 font-medium">
                    <Icon.Info className="w-5 h-5 mb-1 text-gray-300" />
                    <p>확인 사항이 없습니다.</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* ── 업무 목록 ── */}
          <section className="bg-white border h-[26rem] border-[#e1e8ed] rounded-[20px] shadow-sm overflow-hidden flex flex-col min-h-0">
            <div className="px-6 py-3 border-b border-[#e1e8ed] flex justify-between items-center bg-gray-50/50 shrink-0">
              <h2 className="font-bold text-[#2c3e50] text-sm">업무 목록</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    search6();
                    setCompleteDialog(true);
                  }}
                  className="px-4 py-1.5 text-xs font-bold rounded-[10px] border transition-colors bg-white text-[#7f8c8d] border-[#e1e8ed] hover:bg-gray-50"
                >
                  전체보기
                </button>
                <button
                  onClick={handleAddNewTask}
                  className="px-4 py-1.5 text-xs bg-[#77b054] text-white font-bold rounded-[10px] shadow-sm hover:bg-[#5d9040] transition-colors"
                >
                  새 업무 추가
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-auto custom-scrollbar min-h-0 px-3 py-2">
              {workListData.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-400">
                  등록된 업무가 없습니다.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 content-start">
                  {workListData.map((task) => {
                    const priorityInfo =
                      PRIORITY_DISPLAY[task.priorityBit] ?? PRIORITY_DISPLAY[2];
                    const statusInfo =
                      STATUS_DISPLAY[task.statusCd] ?? STATUS_DISPLAY[10];
                    const isDone =
                      task.statusCd === '90' || task.statusCd === 90;
                    const isPinned = !!task.pinYn && task.pinYn === 'Y';
                    const today = dayjs().startOf('day');
                    const dueDate = task.dueDt
                      ? dayjs(task.dueDt).startOf('day')
                      : null;
                    const isOverdue =
                      !isDone && dueDate && dueDate.isBefore(today);
                    const isToday = !isDone && dueDate && dueDate.isSame(today);

                    const isUrgent = Number(task.priorityBit) === 1;
                    const cardBg = isDone
                      ? 'opacity-70'
                      : isUrgent || isOverdue
                        ? 'bg-red-50/40'
                        : isToday
                          ? 'bg-amber-50/40'
                          : 'bg-white';

                    const cardHover =
                      isToday && !isDone
                        ? 'hover:bg-amber-100/80'
                        : isOverdue
                          ? 'hover:bg-red-100/50'
                          : 'hover:bg-gray-50';

                    return (
                      <div
                        key={task.workId}
                        className={`rounded-lg border border-[#e1e8ed] p-2 flex flex-col gap-1 min-w-0 min-h-0 transition-all hover:shadow-sm ${cardBg} ${cardHover}`}
                      >
                        <div className="flex items-center justify-between gap-1.5">
                          <div
                            className="flex items-center gap-0.5 shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isOverdue && (
                              <WarningAmberIcon
                                className="text-red-500"
                                style={{ fontSize: 16 }}
                              />
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const nextPinYn =
                                  task.pinYn === 'Y' ? 'N' : 'Y';
                                handlePinWork(
                                  task.workId,
                                  nextPinYn,
                                  task.priorityBit
                                );
                              }}
                              className={`transition-colors hover:scale-110 ${isPinned ? 'text-blue-500' : 'text-[#e1e8ed] hover:text-blue-400'}`}
                              title={isPinned ? '고정 해제' : '고정'}
                            >
                              {isPinned ? (
                                <PushPinIcon style={{ fontSize: 16 }} />
                              ) : (
                                <PushPinOutlinedIcon style={{ fontSize: 16 }} />
                              )}
                            </button>
                          </div>

                          <div
                            className="relative flex justify-end shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className={`px-1.5 py-0.5 text-[10px] font-bold rounded cursor-pointer flex items-center gap-0.5 ${priorityInfo.badgeClass}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openPriorityMenu === task.workId) {
                                  setOpenPriorityMenu(null);
                                  return;
                                }
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                setPriorityMenuDirection((prev) => ({
                                  ...prev,
                                  [task.workId]:
                                    window.innerHeight - rect.bottom < 130
                                      ? 'up'
                                      : 'down',
                                }));
                                setOpenPriorityMenu(task.workId);
                              }}
                            >
                              {priorityInfo.label}
                              <svg
                                className={`w-2 h-2 shrink-0 transition-transform ${openPriorityMenu === task.workId ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>

                            {openPriorityMenu === task.workId && (
                              <>
                                <div
                                  className="fixed inset-0 z-[100]"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenPriorityMenu(null);
                                  }}
                                />
                                <div
                                  className={`absolute right-0 z-[101] bg-white border border-[#e1e8ed] rounded-[10px] shadow-lg overflow-hidden min-w-[90px] ${priorityMenuDirection[task.workId] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                                >
                                  {[
                                    { key: 1, ...PRIORITY_DISPLAY[1] },
                                    { key: 2, ...PRIORITY_DISPLAY[2] },
                                    { key: 3, ...PRIORITY_DISPLAY[3] },
                                  ].map(({ key, label, badgeClass }) => (
                                    <button
                                      key={key}
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePriorityChange(task.workId, key);
                                        setOpenPriorityMenu(null);
                                      }}
                                      className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                                    >
                                      <span
                                        className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${badgeClass}`}
                                      >
                                        {label}
                                      </span>
                                      {Number(task.priorityBit) === key && (
                                        <svg
                                          className="w-3 h-3 text-[#77b054] ml-auto shrink-0"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2.5}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleTaskClick(task)}
                          className="text-left min-h-0 w-full flex flex-col gap-0.5"
                        >
                          <span className="text-[9px] font-bold text-[#7f8c8d] uppercase tracking-wider">
                            업무 제목
                          </span>
                          <span
                            className={`font-bold text-xs leading-snug text-[#2c3e50] line-clamp-1 break-words ${isDone ? 'line-through text-[#7f8c8d] italic font-medium' : ''}`}
                          >
                            {task.title}
                          </span>
                        </button>

                        <div
                          className="relative flex justify-end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="flex items-center justify-between gap-1.5 min-w-[4.75rem] px-2 py-0.5 bg-white border border-[#e1e8ed] rounded-full hover:border-[#77b054]/50 transition-all shadow-sm group"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openStatusMenu === task.workId) {
                                setOpenStatusMenu(null);
                                return;
                              }
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              const spaceBelow =
                                window.innerHeight - rect.bottom;
                              setMenuDirection((prev) => ({
                                ...prev,
                                [task.workId]: spaceBelow < 120 ? 'up' : 'down',
                              }));
                              setOpenStatusMenu(task.workId);
                            }}
                          >
                            <span
                              className={`px-1.5 py-px rounded-full border text-[9px] font-bold leading-tight ${statusInfo.color}`}
                            >
                              {statusInfo.label}
                            </span>
                            <svg
                              className={`w-2.5 h-2.5 shrink-0 transition-transform text-[#7f8c8d] group-hover:text-[#77b054] ${openStatusMenu === task.workId ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>

                          {openStatusMenu === task.workId && (
                            <>
                              <div
                                className="fixed inset-0 z-[100]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenStatusMenu(null);
                                }}
                              />
                              <div
                                className={`absolute right-0 z-[101] bg-white border border-[#e1e8ed] rounded-[10px] shadow-lg overflow-hidden min-w-[110px]
    ${
      menuDirection[task.workId] === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
    }`}
                              >
                                {[
                                  { key: '10', ...STATUS_DISPLAY[10] },
                                  { key: '20', ...STATUS_DISPLAY[20] },
                                  { key: '90', ...STATUS_DISPLAY[90] },
                                ].map(({ key, label, color }) => (
                                  <button
                                    key={key}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusChange(task.workId, key);
                                      setOpenStatusMenu(null);
                                    }}
                                    className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                                  >
                                    <span
                                      className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${color}`}
                                    >
                                      {label}
                                    </span>
                                    {String(task.statusCd) === key && (
                                      <svg
                                        className="w-3 h-3 text-[#77b054] ml-auto shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2.5}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* ── 우측 컬럼 (4/12) ── */}
        <div className="lg:col-span-4 flex flex-col gap-6 min-h-0">
          {/* 중대재해 처벌법 이행 알림 */}
          <section className="bg-white border border-[#e1e8ed] rounded-[20px] shadow-sm self-start w-full">
            <SectionHeader
              title="중대재해 처벌법 이행 알림"
              onMore={() => navigate('/SeriousAccidentAct')}
            />
            <div className="px-6 pt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              {seriousSummary?.evaDate && (
                <span>
                  최근 평가일:{' '}
                  {dayjs(seriousSummary.evaDate).format('YYYY-MM-DD')}
                </span>
              )}
              {seriousSummary && (
                <span className="font-semibold text-[#2c3e50]">
                  총 취득점수: {seriousSummary.obtainedScore ?? 0}
                  {seriousSummary.totalScore != null
                    ? ` / ${seriousSummary.totalScore}`
                    : ''}
                  점
                </span>
              )}
            </div>
            <div className="px-4 sm:px-6 pb-6 pt-2 w-full h-[18rem] flex items-center justify-center">
              <div className="w-full h-full max-w-[20rem] mx-auto">
                <RadarChart data={seriousData} showEmptySkeleton />
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-6 flex-1 min-h-0">
            {/* 안전 뉴스 */}
            <section className="bg-white border border-[#e1e8ed] rounded-[20px] shadow-sm overflow-hidden flex flex-col h-42.5">
              <SectionHeader title="안전 소식" isOnMore={false} />
              <div className="flex-1 overflow-y-auto p-4 pt-1.5 space-y-3 custom-scrollbar">
                {newsData?.map((news, index) => (
                  <article
                    onClick={() => {
                      if (news.link) {
                        window.open(news.link, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    key={index}
                    className="flex gap-3 group cursor-pointer hover:bg-gray-50 px-2 py-1 transition-all border-b border-[#e1e8ed] last:border-0"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-bold text-[#2c3e50] line-clamp-1 group-hover:text-[#77b054]">
                        {news.title}
                      </h3>
                      <p className="text-[10px] text-gray-300 pt-0.5 line-clamp-1">
                        출처: 고용노동부 보도자료({news.title}/
                        {news.date?.substr(0, 10)})
                      </p>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[10px] text-gray-400">
                          {news.date?.substr(0, 10)}
                        </span>
                        <div className="flex items-center px-1.5 py-0.5 bg-[#77b054]/10 text-[#77b054] text-[10px] font-bold rounded-full">
                          {news.type}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* 공지사항 — 스크롤 영역 고정 높이 */}
            <section className="bg-white border h-[13.75rem] max-h-[13.75rem] min-h-0 shrink-0 border-[#e1e8ed] rounded-[20px] shadow-sm overflow-hidden flex flex-col">
              <div className="px-6 py-3 border-b border-[#e1e8ed] flex justify-between items-center bg-gray-50/50 shrink-0">
                <h2 className="font-bold text-[#2c3e50] text-sm">공지사항</h2>
                <button
                  type="button"
                  onClick={() => navigate('/NoticeBoard')}
                  className="text-[11px] font-bold text-[#77b054] hover:underline flex items-center gap-0.5"
                >
                  전체보기
                  <RightIcon className="text-[10px]! ml-0.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 px-4 py-2 space-y-2 custom-scrollbar">
                {noticeData && noticeData.length > 0 ? (
                  noticeData.map((notice, index) => {
                    const isImportant = notice.noticeYn === 'Y';
                    const gubunBadge = {
                      0: { label: '전체', style: 'bg-blue-50 text-blue-600' },
                      1: {
                        label: '사업장',
                        style: 'bg-green-50 text-green-500',
                      },
                      2: {
                        label: '부서',
                        style: 'bg-purple-100 text-purple-600',
                      },
                    }[notice.gubun] ?? {
                      label: '공지',
                      style: 'bg-gray-100 text-gray-600',
                    };

                    return (
                      <div
                        key={index}
                        className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-1 transition-colors"
                        onClick={() =>
                          navigate('/Notice1', { state: { data: notice } })
                        }
                      >
                        <div className="w-13 h-13 bg-gray-100 rounded-lg shrink-0 overflow-hidden shadow-sm flex items-center justify-center">
                          {notice.thumbnail ? (
                            <img
                              className="w-full h-full object-cover"
                              src={
                                notice.filePath
                                  ? `${Global.file.useUrl}files/${notice.filePath}`
                                  : notice.preview
                              }
                              alt={notice.fileName}
                            />
                          ) : (
                            <Icon.News className="w-6 h-6 text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                            {isImportant && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-600">
                                중요
                              </span>
                            )}
                            <span
                              className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${gubunBadge.style}`}
                            >
                              {gubunBadge.label}
                            </span>
                          </div>
                          <p className="text-xs font-bold text-[#2c3e50] truncate">
                            {notice.title}
                          </p>
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] text-[#77b054] font-medium">
                                {notice.name}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                |
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {notice.gubunDtlNm}
                              </span>
                            </div>
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {notice.endDate?.substr(0, 10)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col gap-1 items-center justify-center h-full text-sm text-gray-400 font-medium">
                    <Icon.Info className="w-5 h-5 mb-1 text-gray-300" />
                    <p>현재 확인 가능한 추가 공지사항이 없습니다.</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* ── 다이얼로그 ── */}
      <ScheduleDetailDialog
        open={scheduleDialog.open}
        onClose={() => setScheduleDialog({ open: false, data: {} })}
        scheduleData={scheduleDialog.data}
      />
      <ChecklistDialog
        open={checklistDialog}
        onClose={() => {
          setChecklistDialog(false);
          setChecklistInitialTabIndex(0);
        }}
        initialActiveTabIndex={checklistInitialTabIndex}
        SaveLogic4={SaveLogic4}
        items={alarmDetailData}
        handleSaveLogic4={handleSaveLogic4}
        onReadAll={handleReadAll}
      />
      <TaskFormDialog
        open={workFormDialog.open}
        onClose={() => setWorkFormDialog({ open: false, data: null })}
        initialData={workFormDialog.data}
        onSave={(formData, workId) => {
          handleSaveWork(formData, workId);
          setWorkFormDialog({ open: false, data: null });
        }}
        onDelete={
          workFormDialog.data?.workId
            ? () => {
                handleDeleteWork(workFormDialog.data.workId);
                setWorkFormDialog({ open: false, data: null });
              }
            : undefined
        }
      />
      <CompleteWorkListDialog
        open={completeDialog}
        onClose={() => setCompleteDialog(false)}
        items={completeWorkListData}
        onDelete={handleDeleteWork}
      />
    </div>
  );
}
